import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  useAlerts,
  useGlobalSettings,
  useHealth,
  type Alert,
  type AlertSeverity,
  type ServiceHealth,
} from "@/api";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { useDocumentTitle } from "@/components/useDocumentTitle";
import { ageSeconds, formatDuration, formatNumber, timeAgo } from "@/lib/format";
import { levelFor } from "@/lib/status";
import { Badge, Card, EmptyState, Skeleton, StatusDot, VisuallyHidden } from "@/ui";
import "./pages.css";

const SERVICE_ORDER = ["listener", "bot", "api", "watchdog"];
const SERVICE_LABEL: Record<string, string> = {
  listener: "Listener",
  bot: "Delivery bot",
  api: "API",
  watchdog: "Watchdog",
};

const SEVERITY_TONE: Record<AlertSeverity, "info" | "warning" | "danger"> = {
  info: "info",
  warning: "warning",
  critical: "danger",
};


export function HealthPage() {
  useDocumentTitle("Health");
  const health = useHealth();
  const alerts = useAlerts(true);
  const settings = useGlobalSettings();

  const threshold = (key: string, fallback: number) => {
    const v = settings.data?.values[key] ?? settings.data?.spec[key]?.default;
    return typeof v === "number" ? v : fallback;
  };
  const maxEntries = threshold("backlog_threshold_entries", 500);
  const maxAge = threshold("backlog_threshold_seconds", 120);
  const q = health.data?.queue;

  return (
    <div className="page">
      <PageHeader
        title="Health"
        description="Is it flowing? Service heartbeats, queue depth and open alerts. Refreshes every 5 s."
        actions={
          health.data ? (
            <Badge tone={health.data.status === "ok" ? "success" : "warning"}>
              System {health.data.status}
            </Badge>
          ) : null
        }
      />
      <ErrorBanner error={health.error} onRetry={() => health.refetch()} />

      <section aria-labelledby="svc-h" className="stack">
        <h2 id="svc-h" className="settings-group__title">
          Services
        </h2>
        <div className="grid-auto">
          {SERVICE_ORDER.map((name) => (
            <ServiceTile
              key={name}
              name={name}
              svc={health.data?.services[name]}
              loading={health.isLoading}
            />
          ))}
        </div>
      </section>

      <section aria-labelledby="queue-h" className="stack">
        <h2 id="queue-h" className="settings-group__title">
          Queue
        </h2>
        <div className="grid-auto">
          <Card>
            <Metric
              label="Pending events"
              value={q ? formatNumber(q.pending) : null}
              level={q ? levelFor(q.pending, maxEntries) : ""}
              hint={`Alert at ${formatNumber(maxEntries)}`}
            />
          </Card>
          <Card>
            <Metric
              label="Oldest pending"
              value={q ? formatDuration(q.oldest_age_s) : null}
              level={q ? levelFor(q.oldest_age_s, maxAge) : ""}
              hint={`Alert at ${formatDuration(maxAge)}`}
            />
          </Card>
          <Card>
            <Metric
              label="Dead letters"
              value={q ? formatNumber(q.dead) : null}
              level={q && q.dead > 0 ? "danger" : ""}
              hint={
                <Link className="link" to="/dead-letters">
                  Review and retry
                </Link>
              }
            />
          </Card>
        </div>
      </section>

      <section aria-labelledby="alerts-h" className="stack">
        <h2 id="alerts-h" className="settings-group__title">
          Open alerts
        </h2>
        <ErrorBanner error={alerts.error} onRetry={() => alerts.refetch()} />
        <Card padding="none">
          {alerts.isLoading ? (
            <div className="health-alerts">
              <Skeleton lines={3} />
            </div>
          ) : alerts.data && alerts.data.items.length > 0 ? (
            <ul className="health-alerts">
              {alerts.data.items.map((a) => (
                <AlertRow key={a.id} alert={a} />
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No open alerts"
              description="Everything the watchdog checks is within its thresholds."
            />
          )}
        </Card>
      </section>
    </div>
  );
}

function ServiceTile({
  name,
  svc,
  loading,
}: {
  name: string;
  svc: ServiceHealth | undefined;
  loading: boolean;
}) {
  const age = ageSeconds(svc?.last_beat);
  const info = svc?.info ?? {};
  const infoEntries = Object.entries(info).slice(0, 3);
  return (
    <Card title={SERVICE_LABEL[name] ?? name}>
      {loading ? (
        <Skeleton lines={2} />
      ) : (
        <div className="stack">
          <div role="status">
            <StatusDot
              status={!svc ? "idle" : svc.alive ? "live" : "dead"}
              label={!svc ? "No heartbeat" : svc.alive ? "Live" : "Down"}
            />
            <span className="faint">
              {" "}
              · beat {age === null ? "never" : `${formatDuration(age)} ago`}
            </span>
          </div>
          {infoEntries.length ? (
            <dl className="kv">
              {infoEntries.map(([k, v]) => (
                <div key={k} style={{ display: "contents" }}>
                  <dt>{k.replace(/_/g, " ")}</dt>
                  <dd>{String(v)}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function Metric({
  label,
  value,
  level,
  hint,
}: {
  label: string;
  value: string | null;
  level: "" | "warning" | "danger";
  hint?: ReactNode;
}) {
  return (
    <div className="metric">
      <span className="metric__label">{label}</span>
      {value === null ? (
        <Skeleton width="6rem" height="2rem" />
      ) : (
        <span className={`metric__value${level ? ` metric__value--${level}` : ""}`}>
          {value}
          {level ? (
            <VisuallyHidden>
              {level === "danger" ? " (over threshold)" : " (rising)"}
            </VisuallyHidden>
          ) : null}
        </span>
      )}
      {hint ? <span className="faint">{hint}</span> : null}
    </div>
  );
}

function AlertRow({ alert }: { alert: Alert }) {
  return (
    <li className="health-alert">
      <Badge tone={SEVERITY_TONE[alert.severity]}>{alert.severity}</Badge>
      <div className="health-alert__body">
        <span>{alert.message}</span>
        <span className="faint">
          {alert.type.replace(/_/g, " ")} · {alert.scope} · opened {timeAgo(alert.opened_at)}
        </span>
      </div>
    </li>
  );
}
